import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { applyActionCode } from "firebase/auth";
import { auth } from "../../firebase/config";

const AuthActionHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState("");

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    const handleAction = async () => {
      if (!mode || !oobCode) {
        console.error("Missing mode or oobCode");
        setError("Invalid or missing action parameters.");
        setProcessing(false);
        return;
      }

      try {
        switch (mode) {
          case "resetPassword":
            navigate(`/reset-password?oobCode=${oobCode}`);
            break;

          case "verifyEmail":
            console.log("Applying action code for email verification...");
            await applyActionCode(auth, oobCode);

            setProcessing(false);

            sessionStorage.setItem("emailVerified", "true");
            navigate("/login");
            break;

          case "recoverEmail":
            navigate(`/recover-email?oobCode=${oobCode}`);
            break;

          default:
            setError(`Unknown action mode: ${mode}`);
            setProcessing(false);
        }
      } catch (error: any) {
        let errorMessage = "Failed to process action.";

        if (error.code === "auth/invalid-action-code") {
          errorMessage = "This link is invalid or has already been used.";
        } else if (error.code === "auth/expired-action-code") {
          errorMessage = "This link has expired. Please request a new one.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
        setProcessing(false);
      }
    };

    handleAction();
  }, [mode, oobCode, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="w-full max-w-md px-4">
          <div className="text-center">
            <h2 className="text-3xl font-semibold mb-4 text-red-600">Error</h2>
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
              role="alert"
            >
              {error}
            </div>
            <button
              onClick={() => navigate("/")}
              className="text-[#1d365d] hover:underline"
            >
              Go to home page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="w-full max-w-md px-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d365d] mb-4"></div>
            <h2 className="text-2xl font-semibold mb-2">Processing...</h2>
            <p className="text-gray-600">
              {mode === "verifyEmail" && "Verifying your email..."}
              {mode === "resetPassword" && "Preparing password reset..."}
              {!mode && "Processing your request..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="w-full max-w-md px-4">
        <div className="text-center">
          <div
            className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4"
            role="alert"
          >
            Action completed successfully!
          </div>
          <p className="text-gray-600">Redirecting...</p>
        </div>
      </div>
    </div>
  );
};

export default AuthActionHandler;
